import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import {
  X,
  Download,
  Upload,
  Save,
  Eye,
  Edit3,
  Copy,
  Check,
  AlertCircle,
  FileJson,
  Settings,
  Trash2,
  Code2,
  Shield,
  Sparkles,
  Zap,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import { Button } from './Button';
import type { VMProtectSettings } from '../../api/builds';
import type { SelectedFunction, ProtectionType } from '../../pages/builds/components/FunctionsTab';

export interface ConfigData {
  savedEngine: string | null;
  savedEngineVersion: string | null;
  savedSettings: VMProtectSettings | null;
  savedFunctions: SelectedFunction[] | null;
}

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  filename: string;
  config: ConfigData;
  onSave: (config: ConfigData) => Promise<void>;
  onClear: () => Promise<void>;
}

// Protection type styling
const getProtectionStyle = (type: ProtectionType) => {
  switch (type) {
    case 'virtualization':
      return {
        bg: 'bg-cyan-500/20',
        border: 'border-cyan-500/30',
        text: 'text-cyan-400',
        icon: Shield,
        label: 'Virtualization',
      };
    case 'mutation':
      return {
        bg: 'bg-purple-500/20',
        border: 'border-purple-500/30',
        text: 'text-purple-400',
        icon: Sparkles,
        label: 'Mutation',
      };
    case 'ultra':
      return {
        bg: 'bg-amber-500/20',
        border: 'border-amber-500/30',
        text: 'text-amber-400',
        icon: Zap,
        label: 'Ultra',
      };
    default:
      return {
        bg: 'bg-slate-500/20',
        border: 'border-slate-500/30',
        text: 'text-slate-400',
        icon: Code2,
        label: 'Unknown',
      };
  }
};

export function ConfigModal({
  isOpen,
  onClose,
  filename,
  config,
  onSave,
  onClear,
}: ConfigModalProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [editedJson, setEditedJson] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [functionsExpanded, setFunctionsExpanded] = useState(true);
  const [functionsSearch, setFunctionsSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Format config for display
  const formatConfig = (cfg: ConfigData): string => {
    return JSON.stringify(cfg, null, 2);
  };

  // Initialize edited JSON when config changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedJson(formatConfig(config));
      setError(null);
      setSuccess(null);
      setMode('view');
    }
  }, [isOpen, config]);

  if (!isOpen) return null;

  const hasConfig = config.savedEngine || config.savedSettings;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([editedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\.[^.]+$/, '')}-config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess('Config downloaded successfully');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        // Validate JSON
        JSON.parse(content);
        setEditedJson(content);
        setMode('edit');
        setError(null);
        setSuccess('Config imported - review and save');
      } catch {
        setError('Invalid JSON file');
      }
    };
    reader.readAsText(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(editedJson) as ConfigData;
      
      // Validate structure
      if (typeof parsed !== 'object' || parsed === null) {
        throw new Error('Config must be an object');
      }

      setSaving(true);
      setError(null);
      await onSave(parsed);
      setSuccess('Config saved successfully');
      setMode('view');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON syntax');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save config');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to clear all saved settings for this file?')) {
      return;
    }

    try {
      setSaving(true);
      await onClear();
      setSuccess('Config cleared');
      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 1000);
    } catch {
      setError('Failed to clear config');
    } finally {
      setSaving(false);
    }
  };

  const handleJsonChange = (value: string) => {
    setEditedJson(value);
    setError(null);
    
    // Validate on change
    try {
      JSON.parse(value);
    } catch {
      // Don't show error while typing
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Settings className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Config Manager</h2>
              <p className="text-sm text-slate-500 font-mono truncate max-w-[300px]">
                {filename}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Messages */}
        {(error || success) && (
          <div className={clsx(
            'mx-4 mt-4 p-3 rounded-xl flex items-center gap-3',
            error ? 'bg-red-500/10 border border-red-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'
          )}>
            {error ? (
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            ) : (
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            )}
            <span className={clsx('text-sm', error ? 'text-red-400' : 'text-emerald-400')}>
              {error || success}
            </span>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-800/50">
          <div className="flex items-center gap-2">
            {/* View/Edit Toggle */}
            <div className="flex items-center bg-slate-800/50 rounded-lg p-1">
              <button
                onClick={() => setMode('view')}
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                  mode === 'view'
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-500 hover:text-white'
                )}
              >
                <Eye className="w-4 h-4" />
                View
              </button>
              <button
                onClick={() => setMode('edit')}
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                  mode === 'edit'
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-500 hover:text-white'
                )}
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              {copied ? 'Copied!' : 'Copy'}
            </button>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all"
              title="Download as JSON"
            >
              <Download className="w-4 h-4" />
              Export
            </button>

            {/* Import Button */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-purple-400 hover:bg-purple-500/10 transition-all"
              title="Import from JSON"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {!hasConfig && mode === 'view' ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4 border border-slate-700/50">
                <FileJson className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No Config Saved</h3>
              <p className="text-slate-500 text-sm mb-6 max-w-sm">
                This file doesn't have any saved configuration yet. You can import a config or create one by building with saved settings.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4" />
                Import Config
              </Button>
            </div>
          ) : (
            <div className="relative space-y-4">
              {/* Config Info Summary */}
              {hasConfig && mode === 'view' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                      <p className="text-xs text-slate-500 mb-1">Engine</p>
                      <p className="text-sm font-medium text-white font-mono">
                        {config.savedEngine || 'Not set'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                      <p className="text-xs text-slate-500 mb-1">Version</p>
                      <p className="text-sm font-medium text-white font-mono">
                        {config.savedEngineVersion || 'Not set'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                      <p className="text-xs text-slate-500 mb-1">Settings</p>
                      <p className="text-sm font-medium text-white font-mono">
                        {config.savedSettings ? Object.keys(config.savedSettings).length + ' options' : 'None'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                      <p className="text-xs text-slate-500 mb-1">Functions</p>
                      <p className="text-sm font-medium text-white font-mono">
                        {config.savedFunctions?.length || 0} selected
                      </p>
                    </div>
                  </div>

                  {/* Selected Functions Section */}
                  {config.savedFunctions && config.savedFunctions.length > 0 && (
                    <div className="rounded-xl border border-slate-700/50 overflow-hidden">
                      {/* Functions Header */}
                      <button
                        onClick={() => setFunctionsExpanded(!functionsExpanded)}
                        className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800/70 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                            <Code2 className="w-4 h-4 text-pink-400" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-white">Selected Functions</p>
                            <p className="text-xs text-slate-500">{config.savedFunctions.length} functions configured</p>
                          </div>
                        </div>
                        {functionsExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-500" />
                        )}
                      </button>

                      {/* Functions List */}
                      {functionsExpanded && (
                        <div className="border-t border-slate-700/50">
                          {/* Search */}
                          <div className="p-3 border-b border-slate-800/50">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                              <input
                                type="text"
                                value={functionsSearch}
                                onChange={(e) => setFunctionsSearch(e.target.value)}
                                placeholder="Search functions..."
                                className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                              />
                            </div>
                          </div>

                          {/* Protection Type Legend */}
                          <div className="px-3 py-2 flex flex-wrap gap-3 border-b border-slate-800/50 bg-slate-900/30">
                            {(['virtualization', 'mutation', 'ultra'] as ProtectionType[]).map((type) => {
                              const style = getProtectionStyle(type);
                              const Icon = style.icon;
                              const count = config.savedFunctions?.filter(f => f.protectionType === type).length || 0;
                              return (
                                <div key={type} className="flex items-center gap-1.5 text-xs">
                                  <Icon className={clsx('w-3 h-3', style.text)} />
                                  <span className={style.text}>{style.label}</span>
                                  <span className="text-slate-500">({count})</span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Functions List */}
                          <div className="max-h-[300px] overflow-y-auto">
                            {config.savedFunctions
                              .filter((func) => {
                                if (!functionsSearch) return true;
                                const search = functionsSearch.toLowerCase();
                                return (
                                  func.name.toLowerCase().includes(search) ||
                                  func.demangledName?.toLowerCase().includes(search) ||
                                  func.rva.toString(16).toLowerCase().includes(search)
                                );
                              })
                              .map((func, index) => {
                                const style = getProtectionStyle(func.protectionType);
                                const Icon = style.icon;
                                return (
                                  <div
                                    key={`${func.rva}-${index}`}
                                    className="flex items-start gap-3 p-3 border-b border-slate-800/30 last:border-b-0 hover:bg-slate-800/30 transition-colors"
                                  >
                                    {/* Protection Icon */}
                                    <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', style.bg)}>
                                      <Icon className={clsx('w-4 h-4', style.text)} />
                                    </div>

                                    {/* Function Info */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-white font-mono break-all leading-relaxed">
                                        {func.demangledName || func.name}
                                      </p>
                                      {func.demangledName && func.demangledName !== func.name && (
                                        <p className="text-xs text-slate-500 font-mono mt-0.5 break-all">
                                          {func.name}
                                        </p>
                                      )}
                                      <div className="flex items-center gap-3 mt-1.5">
                                        <span className="text-xs text-slate-500 font-mono">
                                          RVA: 0x{func.rva.toString(16).toUpperCase()}
                                        </span>
                                        {func.size && (
                                          <span className="text-xs text-slate-500 font-mono">
                                            Size: {func.size} bytes
                                          </span>
                                        )}
                                        {func.complexity && (
                                          <span className="text-xs text-slate-500">
                                            Complexity: {func.complexity}%
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Protection Badge */}
                                    <div className={clsx(
                                      'px-2 py-1 rounded-md text-xs font-medium flex-shrink-0',
                                      style.bg, style.border, style.text, 'border'
                                    )}>
                                      {style.label}
                                    </div>
                                  </div>
                                );
                              })}

                            {/* No results */}
                            {config.savedFunctions.filter((func) => {
                              if (!functionsSearch) return true;
                              const search = functionsSearch.toLowerCase();
                              return (
                                func.name.toLowerCase().includes(search) ||
                                func.demangledName?.toLowerCase().includes(search) ||
                                func.rva.toString(16).toLowerCase().includes(search)
                              );
                            }).length === 0 && (
                              <div className="p-6 text-center">
                                <p className="text-slate-500 text-sm">No functions match your search</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* JSON Editor/Viewer */}
              <div className="relative">
                <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                  <FileJson className="w-4 h-4 text-slate-600" />
                  <span className="text-xs text-slate-600 font-mono">JSON</span>
                </div>
                <textarea
                  value={editedJson}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  readOnly={mode === 'view'}
                  spellCheck={false}
                  className={clsx(
                    'w-full p-4 pt-10 rounded-xl font-mono text-sm',
                    'bg-slate-950 border transition-all',
                    'focus:outline-none focus:ring-2',
                    mode === 'edit'
                      ? 'border-cyan-500/30 focus:border-cyan-500/50 focus:ring-cyan-500/20 text-white h-[400px]'
                      : 'border-slate-800 text-slate-400 cursor-default h-[200px]',
                    'resize-none'
                  )}
                  placeholder="{ }"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-800/50 bg-slate-900/50">
          <div>
            {hasConfig && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={saving}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
                Clear Config
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            {mode === 'edit' && (
              <Button size="sm" onClick={handleSave} loading={saving}>
                <Save className="w-4 h-4" />
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

